import { QUERY } from 'api/Query';
import type { UnresolvedCommitDescriptor } from 'custom-types/UnresolvedCommitDescriptor';
import { type FocusEvent, type FormEvent, useState } from 'react';
import { Assertions } from 'ts/commons/Assertions';
import type { CodeLineSelection } from 'ts/commons/code/CodeLineSelection';
import { openConfirmModal } from 'ts/commons/modal/ConfirmModal';
import { openModal } from 'ts/commons/modal/ModalUtils';
import { StringUtils } from 'ts/commons/StringUtils';
import { TimetravelUtils } from 'ts/commons/TimetravelUtils';
import { UIUtils } from 'ts/commons/UIUtils';
import { Button } from 'ts/components/Button';
import { ModalActionButtons, ScrollArea } from 'ts/components/Modal';
import { TextArea } from 'ts/components/TextArea';

/** A helper class to deal with manually created review comments. */
export class ReviewCommentHandler {
	public constructor(
		private readonly project: string,
		private readonly uniformPath: string,
		private readonly codeLines: string[],
		private readonly commit: UnresolvedCommitDescriptor,
		private readonly selection: CodeLineSelection,
		private readonly reviewFindingAddListener: (message: string, firstLine: number, lastLine: number) => void
	) {}

	/** Opens a modal to allow the user to add a review comment for the current code selection */
	public onAddReviewCommentClicked(): void {
		if (ReviewCommentHandler.isSelectionOnlyEmptyLines(this.codeLines, this.selection)) {
			UIUtils.showInfoDialog('Operation not possible', 'Cannot create review finding for empty line(s).');
			return;
		}
		if (TimetravelUtils.isTimetravelActive()) {
			openConfirmModal({
				title: 'Warning: Timetravel active',
				content: (
					<>
						Timetravel mode is active. Creating a review comment in time-travel mode will insert the review
						finding in the history and <strong>cause a re-analysis of the project</strong>.
					</>
				),
				confirmText: 'Continue anyway',
				destructive: true,
				onConfirm: () => this.openReviewCommentModal()
			});
		} else {
			this.openReviewCommentModal();
		}
	}

	private static isSelectionOnlyEmptyLines(lines: string[], selection: CodeLineSelection): boolean {
		let onlyEmptyLines = true;
		for (let i = selection.firstLine; i <= selection.lastLine; i++) {
			onlyEmptyLines = onlyEmptyLines && StringUtils.isEmptyOrWhitespace(lines[i - 1]);
		}
		return onlyEmptyLines;
	}

	private openReviewCommentModal(): void {
		ReviewCommentHandler.showReviewCommentEditModal('', message => void this.addReviewComment(message));
	}

	/**
	 * Callback for the add review comment action. This will prompt the user for a review comment, submit the comment
	 * via a service call and navigate to the activity perspective afterwards.
	 */
	public async addReviewComment(message: string): Promise<void> {
		await QUERY.createReviewFinding(Assertions.assertString(this.project), { t: this.commit }, [
			{
				uniformPath: Assertions.assertString(this.uniformPath),
				message,
				startLine: this.selection.firstLine,
				endLine: this.selection.lastLine
			}
		]).fetch();
		this.reviewFindingAddListener(message, this.selection.firstLine, this.selection.lastLine);
	}

	/** Shows a modal for editing the review comment */
	public static showReviewCommentEditModal(initialText: string, onSave: (text: string) => void): void {
		openModal({
			title: 'Enter review comment',
			contentRenderer: close => (
				<ReviewCommentModalContent initialText={initialText} onSave={onSave} onClose={close} />
			),
			centered: true,
			size: 'xl',
			scrollAreaComponent: ScrollArea.Autosize
		});
	}
}

type ReviewCommentModalProps = {
	initialText: string;
	onSave: (text: string) => void;
	onClose: () => void;
};

function ReviewCommentModalContent({ initialText, onSave, onClose }: ReviewCommentModalProps) {
	const [reviewComment, setReviewComment] = useState(initialText);

	const onSubmit = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		onSave(reviewComment);
		onClose();
	};

	const selectText = (event: FocusEvent<HTMLTextAreaElement>) => {
		event.target.select();
	};

	return (
		<form className="form" onSubmit={e => onSubmit(e)}>
			<label htmlFor="review-comment-text" className="text-gray-500">
				Please enter a descriptive message for your review comment:
			</label>
			<TextArea
				id="review-comment-text"
				rows={3}
				cols={80}
				defaultValue={initialText}
				className="mt-2 p-2 border border-gray-300 rounded-sm w-full"
				data-autofocus="true"
				onChange={e => setReviewComment(e.target.value)}
				onFocus={selectText}
			/>
			<ModalActionButtons>
				<Button primary content="OK" className="!w-40" type="submit" />
				<Button content="Cancel" className="!w-40" onClick={onClose} type="button" />
			</ModalActionButtons>
		</form>
	);
}
